// A "matrix" = { group, name, type, unit, values: { [targetDate]: [{value, computedDate, properties}] } }

const NUMERIC = "Numeric";

export function isNumericMatrix(m) {
  return (m.type || m.Type) === NUMERIC;
}

// Normalize field access so PascalCase responses don't silently break.
function field(o, name) {
  return o[name] !== undefined
    ? o[name]
    : o[name[0].toUpperCase() + name.slice(1)];
}

export function matrixKey(m) {
  return `${field(m, "group")}/${field(m, "name")}`;
}

// All distinct computedDates across the given matrices, newest first.
export function computedDates(matrices) {
  const set = new Set();
  matrices.forEach((m) => {
    const values = field(m, "values") || {};
    Object.values(values).forEach((arr) =>
      (arr || []).forEach((v) => set.add(field(v, "computedDate")))
    );
  });
  return [...set].sort((a, b) => new Date(b) - new Date(a));
}

// Pick the value for a target date given a computed-run strategy.
// selectedComputed === undefined => latest run for that target date.
function pickForTarget(entries, selectedComputed) {
  if (!entries || entries.length === 0) return undefined;
  if (selectedComputed === undefined) {
    return entries.reduce((best, v) =>
      new Date(field(v, "computedDate")) > new Date(field(best, "computedDate"))
        ? v
        : best
    );
  }
  return entries.find((v) => field(v, "computedDate") === selectedComputed);
}

/**
 * Build Chart.vue chart-data from numeric matrices.
 * @param matrices selected PredictionMatrix[]
 * @param selectedComputed ISO string of a computed run, or undefined for "latest"
 * @returns { "group/name": [[targetDate, number], ...] }
 */
export function predictionsToChartData(matrices, selectedComputed) {
  const out = {};
  matrices.filter(isNumericMatrix).forEach((m) => {
    const values = field(m, "values") || {};
    const tuples = [];
    Object.keys(values).forEach((targetDate) => {
      const v = pickForTarget(values[targetDate], selectedComputed);
      if (v === undefined) return;
      const y = Number(field(v, "value"));
      if (!Number.isNaN(y)) tuples.push([targetDate, y]);
    });
    if (tuples.length > 0) out[matrixKey(m)] = tuples;
  });
  return out;
}

// Overlay: one series per computed run, key suffixed so labels stay distinct.
export function predictionsToOverlay(matrices, runs) {
  const out = {};
  runs.forEach((run) => {
    const part = predictionsToChartData(matrices, run);
    Object.keys(part).forEach((k) => {
      out[`${k} @${run}`] = part[k];
    });
  });
  return out;
}

// Numeric values across all target dates (latest run per target), sorted by
// target date ascending. Used to draw a trend sparkline for a matrix.
function trendValues(m) {
  if (!isNumericMatrix(m)) return [];

  const values = field(m, "values") || {};

  return Object.keys(values)
    .sort((a, b) => new Date(a) - new Date(b))
    .map((targetDate) => {
      const v = pickForTarget(values[targetDate], undefined);
      return v === undefined ? undefined : Number(field(v, "value"));
    })
    .filter((y) => y !== undefined && !Number.isNaN(y));
}

// One row per matrix: the single most recently computed forecast value,
// regardless of which target date it is for. Intended for use with
// pointsAmount=1, where each target already holds only its latest run.
export function latestPredictionRows(matrices) {
  return matrices
    .map((m) => {
      const values = field(m, "values") || {};
      let best;

      Object.keys(values).forEach((targetDate) => {
        (values[targetDate] || []).forEach((v) => {
          if (
            best === undefined ||
            new Date(field(v, "computedDate")) >
              new Date(field(best.value, "computedDate"))
          ) {
            best = { targetDate, value: v };
          }
        });
      });

      if (best === undefined) return undefined;

      return {
        group: field(m, "group"),
        name: field(m, "name"),
        unit: field(m, "unit"),
        type: field(m, "type"),
        targetDate: best.targetDate,
        value: field(best.value, "value"),
        computedDate: field(best.value, "computedDate"),
        trend: trendValues(m),
      };
    })
    .filter((row) => row !== undefined);
}

// Rows for the non-numeric values table.
export function nonNumericRows(matrices, selectedComputed) {
  const rows = [];
  matrices
    .filter((m) => !isNumericMatrix(m))
    .forEach((m) => {
      const values = field(m, "values") || {};
      Object.keys(values).forEach((targetDate) => {
        const v = pickForTarget(values[targetDate], selectedComputed);
        if (v === undefined) return;
        rows.push({
          measure: matrixKey(m),
          type: field(m, "type"),
          targetDate,
          value: field(v, "value"),
          computedDate: field(v, "computedDate"),
        });
      });
    });
  return rows;
}
